/**
 * Custom hook for fetching CMS content from Firestore
 * 
 * This hook provides a consistent way to load CMS-controlled content
 * across all frontend pages, with loading states and error handling.
 */

import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { systemPagesDefaults } from '../cms/systemPagesDefaults';

/**
 * Fetch CMS content by document ID
 * @param {string} contentId - The document ID in cms_content collection
 * @returns {Object} - { content, loading, error }
 */
export function useCMSContent(contentId) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!contentId) {
      setLoading(false);
      return;
    }

    const fetchContent = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const docRef = doc(db, 'cms_content', contentId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setContent(docSnap.data());
        } else {
          // Fallback to defaults if available
          const defaults = systemPagesDefaults[contentId];
          if (defaults) {
            // console.log(`Using default content for: ${contentId}`);
            setContent(defaults);
          } else {
            console.warn(`CMS content not found and no default: ${contentId}`);
            setContent(null);
          }
        }
      } catch (err) {
        console.error(`Error fetching CMS content (${contentId}):`, err);
        // Fallback to defaults on error too
        const defaults = systemPagesDefaults[contentId];
        if (defaults) {
          setContent(defaults);
        } else {
          setError(err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [contentId]);

  return { content, loading, error };
}

/**
 * Fetch multiple CMS content documents at once
 * @param {string[]} contentIds - Array of document IDs
 * @returns {Object} - { contents, loading, error }
 */
export function useCMSContents(contentIds) {
  const [contents, setContents] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!contentIds || contentIds.length === 0) {
      setLoading(false);
      return;
    }

    const fetchContents = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const results = {};
        
        await Promise.all(
          contentIds.map(async (id) => {
            const docRef = doc(db, 'cms_content', id);
            const docSnap = await getDoc(docRef);
            
            if (docSnap.exists()) {
              results[id] = docSnap.data();
            }
          })
        );
        
        setContents(results);
      } catch (err) {
        console.error('Error fetching CMS contents:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchContents();
  }, [JSON.stringify(contentIds)]);

  return { contents, loading, error };
}

export default useCMSContent;

